import { MESSAGES, PASSWORD_REGEX } from '@/public/constants'
import { z } from 'zod'

export const clientLoginSchema = z.object({
  email: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
})

export type TClientLoginFormValues = z.infer<typeof clientLoginSchema>

export const clientAuthResponseSchema = z.object({
  id: z.string(),
  token: z.string(),
  userName: z.string(),
  roles: z.array(z.string()),
  oriCode: z.string().optional(),
  fullName: z.string().optional(),
  tenantId: z.string().optional(),
  lastLoginAt: z.string().optional(),
  email: z.string().email('Please enter a valid email address'),
  requiresDobConfirmation: z.boolean().optional(),
})

export type TClientAuthResponse = z.infer<typeof clientAuthResponseSchema>

export const confirmDobSchema = z.object({
  dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
})

export type TConfirmDobFormValues = z.infer<typeof confirmDobSchema>

export const confirmDobResponseSchema = clientAuthResponseSchema

export type TConfirmDobResponse = z.infer<typeof confirmDobResponseSchema>

export const clientUserSchema = z.object({
  id: z.string(),
  username: z.string(),
  email: z.string().email(),
  roles: z.array(z.string()),
  fullName: z.string().optional(),
  avatar: z.string().optional(),
  active: z.boolean().optional(),
  oriCode: z.string().optional(),
  tenantId: z.string().optional(),
  lastActive: z.string().optional(),
  tenantName: z.string().optional(),
  workstation: z.string().optional(),
  organization: z.string().optional(),
  createdAt: z.string().or(z.date()).optional(),
  updatedAt: z.string().or(z.date()).optional(),
})

export type TClientUserSchema = z.infer<typeof clientUserSchema>

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .refine((val) => PASSWORD_REGEX.test(val), {
        message: MESSAGES.passwordFormat,
      }),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export type TChangePasswordFormValues = z.infer<typeof changePasswordSchema>

export const DEFAULT_CHANGE_PASSWORD_VALUES: TChangePasswordFormValues = {
  newPassword: '',
  currentPassword: '',
  confirmPassword: '',
} as const

export const changePasswordResponseSchema = z.object({
  message: z.string(),
})

export type TChangePasswordResponse = z.infer<typeof changePasswordResponseSchema>

export const verifyChangePasswordSchema = z.object({
  token: z.string().min(1, 'Token is required'),
})

export type TVerifyChangePasswordFormValues = z.infer<typeof verifyChangePasswordSchema>
